import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import ApexCharts from 'apexcharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Definición de tipos
interface DimensionCode {
  code: string;
  dimensionName: string;
  questionName: string;
  valueNow: number;
  valueFuture: number;
}

interface RowspanMap {
  [key: number]: number;
}

@Component({
  selector: 'app-result-dimension',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './result-dimension.component.html',
  styleUrl: './result-dimension.component.scss'
})
export class ResultDimensionComponent implements OnInit, OnChanges {
  @Input() test: any;
  @Output() returnTest: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('contentChart') contentChart!: ElementRef;
  
  chart: ApexCharts | undefined;
  testHoyColor = "#2d5476"; // Azul oscuro
  testFuturoColor = "#a8c5e5"; // Azul claro
  colors: string[] = [this.testHoyColor, this.testFuturoColor];
  dimensionCodes: DimensionCode[] = [];
  categories: string[] = [];
  series: any[] = [];
  dimensionTitles: any[] = [];

  dimensionColorPalette: { [key: string]: { bg: string; text: string } } = {
    'Calidad de Vida': { bg: '#fde7e9', text: '#e41d3f' },
    'Desarrollo Personal': { bg: '#eaf4f9', text: '#2d5476' },
    'Relaciones Personales': { bg: '#fff8e6', text: '#ffc107' },
    'Recursos y Futuro': { bg: '#e6f4e7', text: '#28a745' },
  };
  
  constructor(private renderer: Renderer2) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['test']) {
      this.generateDimensionCodes();
      this.createPolarAreaChart();
    }
  }

  ngOnInit() {
    // this.generateDimensionCodes();
    // this.createPolarAreaChart();
  }

  generateDimensionCodes() {
    // Generar los códigos alfanuméricos para las dimensiones
    this.dimensionCodes = [];
    
    this.test.dimensions.forEach((dimension: any, index: number) => {
      const prefix = String.fromCharCode(65 + index); // A, B, C, etc.
      
      dimension.questions.forEach((question: any, qIndex: number) => {
        const code = `${prefix}${qIndex + 1}`;
        this.dimensionCodes.push({
          code: code,
          dimensionName: dimension.name,
          questionName: question.name,
          valueNow: question.answer.valueNow,
          valueFuture: question.answer.valueFuture
        });
      });
    });
    
    // Establecer categorías (códigos) y series (valores) para el gráfico
    this.categories = this.dimensionCodes.map(item => item.code);
    
    this.series = [
      {
        name: 'Hoy',
        data: this.dimensionCodes.map(item => item.valueNow)
      },
      {
        name: 'Futuro',
        data: this.dimensionCodes.map(item => item.valueFuture)
      }
    ];
  }

  // Método auxiliar para generar código alfanumérico
  getCodeLabel(dimensionIndex: number, questionIndex: number): string {
    // A1, A2, B1, B2, etc.
    const letter = String.fromCharCode(65 + dimensionIndex);
    return letter + (questionIndex + 1);
  }

  getDimensionStyles(dimensionName: string): { [key: string]: string } {
    const colors = this.dimensionColorPalette[dimensionName];
    if (colors) {
      return {
        'background-color': colors.bg,
        'color': colors.text,
      };
    }
    return {
      'background-color': '#F5F5F5',
      'color': '#333',
    };
  }

  calculateTitlePositions(): void {
    if (!this.contentChart?.nativeElement || !this.test.dimensions) return;

    const top = '20%'; // Position titles slightly above the vertical center

    this.dimensionTitles = [];

    // First dimension on the left
    if (this.test.dimensions[0]) {
      this.dimensionTitles.push({
        name: this.test.dimensions[0].name,
        style: {
          position: 'absolute',
          left: '20%',
          top: top,
          transform: 'translate(-50%, -50%)',
          color: this.getDimensionStyles(this.test.dimensions[0].name)['color'],
          fontWeight: 'bold',
          fontSize: '12px',
        }
      });
    }

    // Second dimension on the right
    if (this.test.dimensions[1]) {
      this.dimensionTitles.push({
        name: this.test.dimensions[1].name,
        style: {
          position: 'absolute',
          right: '20%',
          top: top,
          transform: 'translate(50%, -50%)',
          color: this.getDimensionStyles(this.test.dimensions[1].name)['color'],
          fontWeight: 'bold',
          fontSize: '14px',
        }
      });
    }
  }

  createPolarAreaChart() {
    if (this.chart) {
      this.chart.destroy();
    }
    const options = {
      series: this.series,
      chart: {
        type: "radar",
        height: 500,
        fontFamily: 'Helvetica, Arial, sans-serif',
        background: "transparent",
        dropShadow: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        },
        events: {
          mounted: () => {
            this.calculateTitlePositions();
          },
          updated: () => {
            this.calculateTitlePositions();
          }
        }
      },
      stroke: {
        width: 2,
        curve: 'straight',
        lineCap: 'butt'
      },
      fill: {
        opacity: 0.2
      },
      legend: {
        show: false
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        radar: {
          size: 140,
          offsetX: 0,
                    offsetY: 0,
          polygons: {
            strokeColors: "#e1e1e1",
            strokeWidth: 1,
            connectorColors: "#e1e1e1",
            fill: {
              colors: ["#f8f8f8", "#fff"]
            }
          }
        }
      },
      colors: this.colors,
      markers: {
        size: 4,
        colors: ["#fff"],
        strokeColors: [this.testHoyColor, this.testFuturoColor],
        strokeWidth: 2,
        hover: {
          size: 5
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val: any) {
            return val;
          }
        }
      },
      xaxis: {
        categories: this.categories,
        labels: {
          show: true,
          style: {
            fontSize: '12px',
            fontWeight: 400
          },
          formatter: function(val: string) {
            return val;
          }
        }
      },
      yaxis: {
        show: true,
        max: 10,
        min: 0,
        tickAmount: 5,
        labels: {
          style: {
            colors: "#999",
            fontSize: '11px'
          },
          formatter: function(val: number) {
            return val.toFixed(0);
          }
        }
      },
      grid: {
        padding: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        }
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            y2: 10,
            strokeDashArray: 0,
            borderColor: '#000',
            borderWidth: 1,
            opacity: 0.3
          }
        ]
      },
      theme: {
        monochrome: {
          enabled: false
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 380
            }
          }
        }
      ]
    };

    this.chart = new ApexCharts(document.querySelector("#chart"), options);
    this.chart.render();
  }

  downloadPdf(): void {
    const doc = new jsPDF();

    // Título del documento
    doc.setFontSize(16);
    doc.setTextColor(45, 84, 118); // Color #2d5476
    doc.text('Gráfico comparativo', 14, 20);

    // Agregar línea divisoria
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 25, 195, 25);
    
    // Agregar fecha
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const today = new Date();
    const dateStr = today.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Fecha: ${dateStr}`, 14, 35);

    // Configuración de la tabla
    const tableData: string[][] = [];
    const dimensionRowspans: RowspanMap = {};
    
    // Procesar datos para la tabla
    this.test.dimensions.forEach((dimension: any, dimensionIndex: number) => {
      const prefixLetter = String.fromCharCode(65 + dimensionIndex); // A, B, C, etc.
      let firstRow = true;
      
      dimension.questions.forEach((question: any, qIndex: number) => {
        const codeLabel = `${prefixLetter}${qIndex + 1}`;
        const fullLabel = `${codeLabel} - ${question.name}`;
        
        if (firstRow) {
          tableData.push([
            dimension.name,
            fullLabel,
            question.answer.valueNow.toString(),
            question.answer.valueFuture.toString()
          ]);
          dimensionRowspans[tableData.length - 1] = dimension.questions.length;
          firstRow = false;
        } else {
          tableData.push([
            '',
            fullLabel,
            question.answer.valueNow.toString(),
            question.answer.valueFuture.toString()
          ]);
        }
      });
    });

    // Cabecera de la tabla
    const tableHeaders = [['Dimensión', 'Subdimensión', 'Hoy', '7 Años']];

    autoTable(doc, {
      head: tableHeaders,
      body: tableData,
      startY: 45,
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [45, 84, 118], // #2d5476
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [242, 247, 253] }, // #f2f7fd
        2: { halign: 'center', fontStyle: 'bold', textColor: [45, 84, 118] }, // #2d5476
        3: { halign: 'center', fontStyle: 'bold', textColor: [106, 152, 199] } // #6a98c7
      },
      didParseCell: function(data: any) {
        // Aplicar rowspan a las celdas de dimensión
        if (data.column.index === 0 && dimensionRowspans[data.row.index]) {
          data.cell.rowSpan = dimensionRowspans[data.row.index];
        }
        
        // Alternar colores de fondo para las filas
        if (data.row.index % 2 === 0 && data.column.index > 0) {
          data.cell.styles.fillColor = [250, 250, 250];
        }
      }
    });

    // Guardar el PDF
    doc.save('grafico_comparativo.pdf');
  }

  return() {
    this.returnTest.emit();
  }
}